import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const galleryImages = [
  {
    id: 1,
    src: "https://customer-assets.emergentagent.com/job_soulful-beats-3/artifacts/853jv1wi_WhatsApp%20Image%202026-01-10%20at%2023.16.20.jpeg",
    title: "Studio Session",
    description: "Morena TheDeeJay in the studio crafting beats"
  },
  {
    id: 2,
    src: "https://customer-assets.emergentagent.com/job_soulful-beats-3/artifacts/z6avf6ub_WhatsApp%20Image%202026-01-10%20at%2023.16.21%20%281%29.jpeg",
    title: "Beach Vibes",
    description: "Sunset set at the beach"
  },
  {
    id: 3,
    src: "https://customer-assets.emergentagent.com/job_soulful-beats-3/artifacts/cyb14fzy_WhatsApp%20Image%202026-01-10%20at%2023.16.21%20%282%29.jpeg",
    title: "Club Night",
    description: "Live performance at the club"
  },
  {
    id: 4,
    src: "https://customer-assets.emergentagent.com/job_soulful-beats-3/artifacts/bptf2aep_WhatsApp%20Image%202026-01-10%20at%2023.16.22.jpeg",
    title: "Beach Festival",
    description: "Bringing Deep House to the crowd"
  },
  {
    id: 5,
    src: "https://customer-assets.emergentagent.com/job_soulful-beats-3/artifacts/7t4cq5g4_WhatsApp%20Image%202026-01-10%20at%2023.16.22%20%281%29.jpeg",
    title: "Golden Hour",
    description: "Sunset vibes at the beach party"
  }
];

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <section id="gallery" className="py-24 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="service-tag" style={{ backgroundColor: '#78d692', color: '#151515' }}>
            Behind The Scenes
          </span>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-white mt-4 mb-6">
            Gallery
          </h2>
          <p className="text-white/60 text-lg lg:text-xl max-w-2xl">
            From studio sessions to beach festivals — capturing the essence of LoxitonDeepHouse.
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6"
        >
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              variants={itemVariants}
              className={`group relative cursor-pointer overflow-hidden rounded-lg ${
                index === 0 ? 'col-span-2 row-span-2' : ''
              }`}
              onClick={() => openLightbox(index)}
            >
              <div className={`relative ${index === 0 ? 'aspect-square' : 'aspect-square'}`}>
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                    <ZoomIn className="w-10 h-10 text-white mx-auto mb-2" />
                    <p className="text-white font-display text-lg uppercase">{image.title}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 backdrop-blur-lg flex items-center justify-center"
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-50"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors z-50 p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors z-50 p-2 rounded-full bg-white/10 hover:bg-white/20"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Image */}
            <motion.div
              key={selectedImage.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[85vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
              <div className="text-center mt-6">
                <h3 className="font-display text-2xl font-bold text-white uppercase mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-white/60">{selectedImage.description}</p>
                <p className="text-white/40 text-sm mt-2 font-mono">
                  {currentIndex + 1} / {galleryImages.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
